import { audio } from '../../../audio/AudioManager.js';
import { SaveManager } from '../../../state/SaveManager.js';
import { addEnergy, ENERGY_PER_SEGMENT } from '../../../progression/WorldEnergy.js';
import { NUMBER_KINGDOM_ASSETS } from './NumberKingdomAssets.js';
import {
  NUMBER_KINGDOM_DISTRICTS,
  completeFirstStarBridgeRepair,
  completeSecondStarBridgeRepair,
  completeStarBridgeRestoration,
  getNumberKingdomState,
  markStarBridgeDiscovered,
  visitNumberKingdomDistrict,
  type NumberKingdomStateSnapshot,
  type StarBridgeState,
} from './NumberKingdomState.js';
import { ArrivalInputController } from './components/ArrivalInputController.js';
import { CrystalNumberBuilder } from './minigames/CrystalNumberBuilder.js';
import { NumberPathChallenge } from './minigames/NumberPathChallenge.js';
import { NumberCoreChallenge } from './minigames/NumberCoreChallenge.js';
import {
  STAR_BRIDGE_PLACE_VALUE_1,
  type CrystalNumberBuilderConfig,
} from './minigames/CrystalNumberBuilderConfig.js';
import {
  STAR_BRIDGE_NUMBER_PATH_2,
  type NumberPathConfig,
} from './minigames/NumberPathConfig.js';
import {
  STAR_BRIDGE_NUMBER_CORE_3,
  type NumberCoreChallengeConfig,
} from './minigames/NumberCoreChallengeConfig.js';
import {
  RESTORED_BRIDGE_EXIT_EVENT,
  resolveRestoredBridgeExit,
} from './districts/star-bridge/RestoredBridgeExit.js';
import {
  getSceneResponsiveLayout,
  type ResponsiveLayoutConfig,
} from '../../shared/layout/index.js';
import { applicationLifecycle } from '../../shared/lifecycle/index.js';
import { getNumberKingdomDistrict } from './districts/NumberKingdomDistrictRegistry.js';
import {
  createNumberKingdomHomeButton,
  type NumberKingdomHomeButton,
} from './components/NumberKingdomHomeButton.js';

const LAYOUT_W = 1280;
const LAYOUT_H = 720;
const FONT_UI = '"Nunito", "Segoe UI", Arial, sans-serif';
const COSMO_START_X = 420;
const COSMO_BASELINE_Y = 587;
const BRIDGE_X = 748;
const BRIDGE_Y = 392;
const BRIDGE_WIDTH = 350;
const DISCOVERY_X = 718;
const CONSOLE_X = 920;
const CONSOLE_Y = 592;
const CONSOLE_INTERACTION_X = 830;
const NUMBER_PATH_X = LAYOUT_W * 0.53;
const NUMBER_PATH_Y = LAYOUT_H * 0.845;
const NUMBER_PATH_INTERACTION_RADIUS = 115;
const NUMBER_CORE_X = LAYOUT_W * 0.39;
const NUMBER_CORE_Y = LAYOUT_H * 0.845;
const NUMBER_CORE_INTERACTION_RADIUS = 120;
const BRIDGE_EXIT_INTERACTION_RADIUS = 135;
const COSMO_WIDTH = 258;
const COSMO_HEIGHT = 230;
const NUMBER_KINGDOM_EXPLORATION_LAYOUT: ResponsiveLayoutConfig = {
  logicalSize: { width: LAYOUT_W, height: LAYOUT_H },
  visibilityScaleMode: 'envelop',
};

const BRIDGE_TEXTURES: Record<StarBridgeState, string> = {
  broken: NUMBER_KINGDOM_ASSETS.starBridgeBroken.key,
  'repair-1': NUMBER_KINGDOM_ASSETS.starBridgeRepair1.key,
  'repair-2': NUMBER_KINGDOM_ASSETS.starBridgeRepair2.key,
  'repair-3': NUMBER_KINGDOM_ASSETS.starBridgeRepair3.key,
  restored: NUMBER_KINGDOM_ASSETS.starBridgeRestored.key,
};

export class NumberKingdomScene extends Phaser.Scene {
  private renderScale = 1;
  private reducedMotion = false;
  private controlsReady = false;
  private returningToHub = false;
  private discoveryRunning = false;
  private missionVisible = false;
  private nearNumberBuilder = false;
  private nearNumberPath = false;
  private nearNumberCore = false;
  private nearBridgeExit = false;
  private numberCoreRevealRunning = false;
  private numberCoreRevealed = false;
  private repairSequenceRunning = false;
  private resizeDebounce?: number;
  private unsubscribeLifecycle?: () => void;

  private distantLayer!: Phaser.GameObjects.Container;
  private midgroundLayer!: Phaser.GameObjects.Container;
  private gameplayLayer!: Phaser.GameObjects.Container;
  private characterLayer!: Phaser.GameObjects.Container;
  private interactableLayer!: Phaser.GameObjects.Container;
  private foregroundLayer!: Phaser.GameObjects.Container;
  private uiLayer!: Phaser.GameObjects.Container;

  private arrivalPlaza!: Phaser.GameObjects.Image;
  private arrivalMagic!: Phaser.GameObjects.Image;
  private bridgeGlow!: Phaser.GameObjects.Image;
  private bridgeImage!: Phaser.GameObjects.Image;
  private bridgeExitZone!: Phaser.GameObjects.Zone;
  private numberBuilderConsole!: Phaser.GameObjects.Image;
  private numberPathPlatform!: Phaser.GameObjects.Image;
  private numberCoreMachine!: Phaser.GameObjects.Image;
  private worldCoreCrystal!: Phaser.GameObjects.Image;
  private cosmo!: Phaser.GameObjects.Image;
  private homeButton!: NumberKingdomHomeButton;
  private districtTitle!: Phaser.GameObjects.Text;
  private controlsHint!: Phaser.GameObjects.Text;
  private worldTitleCard!: Phaser.GameObjects.Image;
  private reactionText!: Phaser.GameObjects.Text;
  private missionPanel!: Phaser.GameObjects.Image;
  private inputController!: ArrivalInputController;
  private activeBuilder?: CrystalNumberBuilder;
  private activeNumberPath?: NumberPathChallenge;
  private activeNumberCore?: NumberCoreChallenge;
  private consolePulse?: Phaser.Tweens.Tween;
  private worldState!: NumberKingdomStateSnapshot;

  constructor() { super({ key: 'NumberKingdom' }); }

  create() {
    const resumeState = getNumberKingdomState();
    const resumeDistrict = getNumberKingdomDistrict(resumeState.currentDistrict);
    if (resumeState.starBridgeDiscovered
      && resumeDistrict.implementation === 'playable'
      && resumeDistrict.sceneKey
      && resumeDistrict.sceneKey !== 'NumberKingdom') {
      this.scene.start(resumeDistrict.sceneKey);
      return;
    }

    void audio.enterSoundscape('number-kingdom');
    this.renderScale = this.configureCrispRenderer();
    this.reducedMotion = SaveManager.getSettings().reducedMotion
      || (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
    this.controlsReady = false;
    this.returningToHub = false;
    this.discoveryRunning = false;
    this.missionVisible = false;
    this.nearNumberBuilder = false;
    this.nearNumberPath = false;
    this.nearNumberCore = false;
    this.nearBridgeExit = false;
    this.numberCoreRevealRunning = false;
    this.numberCoreRevealed = false;
    this.repairSequenceRunning = false;

    const previousState = getNumberKingdomState();
    const district = previousState.starBridgeDiscovered
      ? previousState.currentDistrict
      : NUMBER_KINGDOM_DISTRICTS.arrivalValley;
    const arrival = visitNumberKingdomDistrict(district);
    this.worldState = arrival.state;

    this.createLayerArchitecture();
    this.createArrivalEnvironment();
    this.createSharedCosmo();
    this.createExplorationFoundation();
    this.createNavigationUI();
    this.createDiscoveryUI();
    this.registerLifecycleSafety();
    this.playArrivalReveal(arrival.firstVisit);
  }

  private configureCrispRenderer() {
    const parentW = this.scale.parentSize.width || window.innerWidth;
    const parentH = this.scale.parentSize.height || window.innerHeight;
    const cssScale = Math.max(parentW / LAYOUT_W, parentH / LAYOUT_H);
    const dpr = Phaser.Math.Clamp(window.devicePixelRatio || 1, 1, 2);
    const renderScale = Phaser.Math.Clamp(cssScale * dpr, 1, 2);

    this.scale.scaleMode = Phaser.Scale.ENVELOP;
    this.scale.autoCenter = Phaser.Scale.NO_CENTER;
    this.scale.displaySize.setAspectMode(Phaser.Scale.ENVELOP);
    this.scale.setGameSize(
      Math.round(LAYOUT_W * renderScale),
      Math.round(LAYOUT_H * renderScale),
    );
    this.scale.refresh();
    this.game.canvas.style.marginLeft = '0px';
    this.game.canvas.style.marginTop = '0px';
    this.cameras.main.setZoom(renderScale).centerOn(LAYOUT_W * 0.5, LAYOUT_H * 0.5);
    return renderScale;
  }

  private createLayerArchitecture() {
    this.distantLayer = this.add.container(0, 0).setDepth(0);
    this.midgroundLayer = this.add.container(0, 0).setDepth(10);
    this.gameplayLayer = this.add.container(0, 0).setDepth(20);
    this.characterLayer = this.add.container(0, 0).setDepth(30);
    this.interactableLayer = this.add.container(0, 0).setDepth(60);
    this.foregroundLayer = this.add.container(0, 0).setDepth(50);
    this.uiLayer = this.add.container(0, 0).setDepth(100);

    this.midgroundLayer.setName('number-kingdom-midground');
    this.interactableLayer.setName('number-kingdom-interactables');
    this.foregroundLayer.setName('number-kingdom-foreground');
  }

  private createArrivalEnvironment() {
    const background = this.add.image(
      LAYOUT_W * 0.5,
      LAYOUT_H * 0.5,
      NUMBER_KINGDOM_ASSETS.arrivalBackground.key,
    );
    const coverScale = Math.max(LAYOUT_W / background.width, LAYOUT_H / background.height);
    background.setDisplaySize(background.width * coverScale, background.height * coverScale);
    this.distantLayer.add(background);

    this.bridgeGlow = this.add.image(
      BRIDGE_X + 2,
      BRIDGE_Y + 12,
      NUMBER_KINGDOM_ASSETS.bridgeMagicGlow.key,
    ).setAlpha(this.worldState.starBridgeDiscovered ? 0.07 : 0);
    this.bridgeGlow.setCrop(20, 22, 305, 245);
    this.bridgeGlow.setDisplaySize(225, 186);

    this.bridgeImage = this.add.image(BRIDGE_X, BRIDGE_Y, BRIDGE_TEXTURES.broken)
      .setAlpha(this.worldState.starBridgeDiscovered ? 1 : 0.88);
    this.midgroundLayer.add([this.bridgeGlow, this.bridgeImage]);
    this.setStarBridgeState(this.worldState.starBridgeState);

    this.bridgeExitZone = this.add.zone(BRIDGE_X, BRIDGE_Y, BRIDGE_WIDTH, 190)
      .setName('restored-star-bridge-exit');
    this.bridgeExitZone.on('pointerup', () => this.tryUseRestoredBridgeExit(true));
    this.interactableLayer.add(this.bridgeExitZone);

    this.arrivalPlaza = this.add.image(
      LAYOUT_W * 0.5,
      620,
      NUMBER_KINGDOM_ASSETS.arrivalPlaza.key,
    );
    const plazaWidth = 1000;
    this.arrivalPlaza.setDisplaySize(
      plazaWidth,
      plazaWidth * (this.arrivalPlaza.height / this.arrivalPlaza.width),
    );
    this.gameplayLayer.add(this.arrivalPlaza);

    this.arrivalMagic = this.add.image(
      COSMO_START_X,
      COSMO_BASELINE_Y - 92,
      NUMBER_KINGDOM_ASSETS.arrivalMagic.key,
    ).setAlpha(0);
    this.arrivalMagic.setCrop(12, 24, 345, 242);
    this.arrivalMagic.setDisplaySize(220, 170);
    this.gameplayLayer.add(this.arrivalMagic);

    this.numberBuilderConsole = this.add.image(
      CONSOLE_X,
      CONSOLE_Y,
      NUMBER_KINGDOM_ASSETS.numberBuilderConsole.key,
    ).setOrigin(0.5, 1).setAlpha(this.worldState.starBridgeDiscovered ? 1 : 0.42);
    this.numberBuilderConsole.setCrop(5, 2, 505, 280);
    this.numberBuilderConsole.setDisplaySize(190, 116);
    this.numberBuilderConsole.setInteractive({ useHandCursor: true });
    this.numberBuilderConsole.on('pointerup', () => this.tryActivateNumberBuilder(true));
    this.interactableLayer.add(this.numberBuilderConsole);

    this.numberPathPlatform = this.add.image(
      NUMBER_PATH_X,
      NUMBER_PATH_Y,
      NUMBER_KINGDOM_ASSETS.numberPathPlatform.key,
    ).setOrigin(0.5, 1).setAlpha(0);
    this.numberPathPlatform.setCrop(0, 0, 350, 240);
    this.numberPathPlatform.setDisplaySize(174, 119);
    this.numberPathPlatform.setInteractive({ useHandCursor: true });
    this.numberPathPlatform.on('pointerup', () => this.tryActivateNumberPath(true));
    this.interactableLayer.add(this.numberPathPlatform);

    this.numberCoreMachine = this.add.image(
      NUMBER_CORE_X,
      NUMBER_CORE_Y,
      NUMBER_KINGDOM_ASSETS.numberCoreMachine.key,
    ).setOrigin(0.5, 1).setAlpha(0);
    this.numberCoreMachine.setCrop(0, 0, 320, 350);
    this.numberCoreMachine.setDisplaySize(154, 168);
    this.numberCoreMachine.setInteractive({ useHandCursor: true });
    this.numberCoreMachine.on('pointerup', () => this.tryActivateNumberCore(true));

    this.worldCoreCrystal = this.add.image(
      NUMBER_CORE_X,
      NUMBER_CORE_Y - 94,
      NUMBER_KINGDOM_ASSETS.coreEnergyCrystal.key,
    ).setAlpha(0).setVisible(false);
    this.worldCoreCrystal.setCrop(20, 0, 178, 287);
    this.worldCoreCrystal.setDisplaySize(65, 105);
    this.interactableLayer.add([this.numberCoreMachine, this.worldCoreCrystal]);
    this.updateChallengeAvailability();

    const leftFoliage = this.add.image(
      -18,
      LAYOUT_H + 5,
      NUMBER_KINGDOM_ASSETS.foregroundFoliageLeft.key,
    ).setOrigin(0, 1).setAlpha(0.82);
    leftFoliage.setCrop(0, 25, 450, 222);
    leftFoliage.setDisplaySize(310, 189);

    const rightFoliage = this.add.image(
      LAYOUT_W + 16,
      LAYOUT_H + 4,
      NUMBER_KINGDOM_ASSETS.foregroundFoliageRight.key,
    ).setOrigin(1, 1).setAlpha(0.82);
    rightFoliage.setCrop(36, 29, 360, 229);
    rightFoliage.setDisplaySize(288, 196);
    this.foregroundLayer.add([leftFoliage, rightFoliage]);
  }

  private createSharedCosmo() {
    this.cosmo = this.add.image(
      COSMO_START_X,
      COSMO_BASELINE_Y,
      NUMBER_KINGDOM_ASSETS.numberKingdomCosmo.key,
    )
      .setOrigin(0.5, 1)
      .setDisplaySize(COSMO_WIDTH, COSMO_HEIGHT);
    this.characterLayer.add(this.cosmo);
  }

  private createExplorationFoundation() {
    this.inputController = new ArrivalInputController(this, this.cosmo, {
      minX: 310,
      maxX: 970,
      baselineY: COSMO_BASELINE_Y,
      speed: 190,
      depth: 45,
      onPositionChanged: x => this.handleExplorationPosition(x),
    });

    this.registry.set('numberKingdom:starBridgeState', this.worldState.starBridgeState);
    this.registry.set('numberKingdom:starBridgeDiscovered', this.worldState.starBridgeDiscovered);
    this.registry.set('numberKingdom:currentDistrict', this.worldState.currentDistrict);
    this.registry.set('numberKingdom:bridgeCrossingUnlocked', this.worldState.bridgeCrossingUnlocked);
    this.updateRestoredBridgeExitAvailability();
  }

  private createNavigationUI() {
    const parentW = this.scale.parentSize.width || window.innerWidth;
    const parentH = this.scale.parentSize.height || window.innerHeight;
    const safeInsetX = Math.max(0, (LAYOUT_W - LAYOUT_H * (parentW / parentH)) * 0.5);
    this.homeButton = createNumberKingdomHomeButton(this, () => this.returnToHub());

    this.districtTitle = this.add.text(LAYOUT_W * 0.5, 25, 'Arrival Valley', {
      fontFamily: FONT_UI,
      fontSize: '20px',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#244526',
      strokeThickness: 5,
      shadow: { offsetX: 0, offsetY: 2, blur: 3, color: '#17351f', fill: true },
    }).setOrigin(0.5, 0);

    this.controlsHint = this.add.text(
      LAYOUT_W - 18 - safeInsetX,
      20,
      'Explore:  ← →  /  A D  /  Tap the plaza', {
        fontFamily: FONT_UI,
        fontSize: '13px',
        fontStyle: 'bold',
        color: '#fffbe8',
        padding: { x: 12, y: 8 },
        stroke: '#17351f',
        strokeThickness: 4,
        shadow: { offsetX: 0, offsetY: 2, blur: 3, color: '#0b2515', fill: true },
      },
    ).setOrigin(1, 0);

    // Keep global navigation outside uiLayer: challenge and arrival sequences fade that
    // layer, while the Home Hub control must remain visible above every playable state.
    this.uiLayer.add([this.districtTitle, this.controlsHint]);
    this.positionSafeUI();
    this.input.keyboard?.on('keydown-ESC', this.handleEscape, this);
    this.input.keyboard?.on('keydown-E', this.handleInteractKeyboard, this);
  }

  private createDiscoveryUI() {
    this.worldTitleCard = this.add.image(
      LAYOUT_W * 0.5,
      178,
      NUMBER_KINGDOM_ASSETS.numberKingdomTitle.key,
    ).setAlpha(0).setScale(0.88);
    this.worldTitleCard.setCrop(6, 7, 433, 298);
    this.worldTitleCard.setDisplaySize(370, 270);

    this.reactionText = this.add.text(LAYOUT_W * 0.5, 93, '', {
      fontFamily: FONT_UI,
      fontSize: '23px',
      fontStyle: 'bold',
      align: 'center',
      color: '#fff8d0',
      stroke: '#17351f',
      strokeThickness: 7,
      shadow: { offsetX: 0, offsetY: 3, blur: 5, color: '#0b2515', fill: true },
      wordWrap: { width: 650 },
    }).setOrigin(0.5, 0).setAlpha(0);

    this.missionPanel = this.add.image(
      LAYOUT_W * 0.5,
      LAYOUT_H * 0.5 + 8,
      NUMBER_KINGDOM_ASSETS.starBridgeMissionPanel.key,
    ).setAlpha(0).setScale(0.94).setVisible(false);
    this.missionPanel.setCrop(8, 7, 520, 312);
    this.missionPanel.setDisplaySize(520, 339);
    this.missionPanel.on('pointerup', this.dismissMissionPanel, this);

    this.uiLayer.add([this.worldTitleCard, this.reactionText, this.missionPanel]);
  }

  private playArrivalReveal(firstVisit: boolean) {
    const camera = this.cameras.main;
    const revealDuration = this.reducedMotion ? 320 : firstVisit ? 1650 : 650;
    const plazaY = this.arrivalPlaza.y;

    this.distantLayer.setAlpha(0);
    this.midgroundLayer.setAlpha(0);
    this.gameplayLayer.setAlpha(0);
    this.characterLayer.setAlpha(0);
    this.foregroundLayer.setAlpha(0);
    this.districtTitle.setAlpha(firstVisit ? 0 : 1);
    if (!this.reducedMotion && firstVisit) {
      camera.setZoom(this.renderScale * 1.045).centerOn(LAYOUT_W * 0.5, LAYOUT_H * 0.47);
      this.arrivalPlaza.y = plazaY + 14;
      this.cosmo.x -= 28;
    }

    camera.fadeIn(this.reducedMotion ? 220 : 520, 255, 248, 220);
    this.tweens.add({
      targets: [this.distantLayer, this.midgroundLayer],
      alpha: 1,
      duration: this.reducedMotion ? 180 : 520,
      ease: 'Sine.easeOut',
    });

    if (!this.reducedMotion && firstVisit) {
      camera.zoomTo(this.renderScale, revealDuration, 'Sine.easeInOut');
      camera.pan(LAYOUT_W * 0.5, LAYOUT_H * 0.5, revealDuration, 'Sine.easeInOut');
    }

    this.time.delayedCall(this.reducedMotion ? 100 : firstVisit ? 430 : 150, () => {
      this.tweens.add({
        targets: [this.gameplayLayer, this.foregroundLayer],
        alpha: 1,
        duration: this.reducedMotion ? 180 : 520,
        ease: 'Sine.easeOut',
      });
      this.tweens.add({
        targets: this.arrivalPlaza,
        y: plazaY,
        duration: this.reducedMotion ? 1 : 700,
        ease: 'Sine.easeOut',
      });
    });

    this.time.delayedCall(this.reducedMotion ? 190 : firstVisit ? 760 : 260, () => {
      this.tweens.add({
        targets: this.characterLayer,
        alpha: 1,
        duration: this.reducedMotion ? 160 : 430,
        ease: 'Sine.easeOut',
      });
      if (!this.reducedMotion && firstVisit) {
        this.arrivalMagic.setAlpha(0.5).setScale(0.92);
        this.tweens.add({
          targets: this.arrivalMagic,
          alpha: 0,
          scale: 1.04,
          duration: 900,
          ease: 'Sine.easeOut',
        });
        this.tweens.add({ targets: this.cosmo, x: COSMO_START_X, duration: 650, ease: 'Sine.easeOut' });
      }
    });

    if (firstVisit) this.playWorldTitleReveal();
    const unlockDelay = this.reducedMotion ? 620 : firstVisit ? 2050 : 720;
    this.time.delayedCall(unlockDelay, () => {
      this.districtTitle.setAlpha(1);
      this.controlsReady = true;
      this.inputController.enable();
    });
  }

  private playWorldTitleReveal() {
    const fadeIn = this.reducedMotion ? 220 : 360;
    const hold = this.reducedMotion ? 680 : 1050;
    const fadeOut = this.reducedMotion ? 240 : 430;
    this.worldTitleCard.setAlpha(0).setScale(this.reducedMotion ? 1 : 0.91);
    this.tweens.add({
      targets: this.worldTitleCard,
      alpha: 1,
      scale: 1,
      duration: fadeIn,
      ease: 'Sine.easeOut',
      onComplete: () => {
        this.time.delayedCall(hold, () => {
          this.tweens.add({
            targets: this.worldTitleCard,
            alpha: 0,
            duration: fadeOut,
            ease: 'Sine.easeIn',
          });
        });
      },
    });
  }

  private handleExplorationPosition(x: number) {
    this.checkDiscoveryTrigger(x);
    const shouldBeNearBuilder = this.controlsReady
      && this.isNumberBuilderAvailable()
      && x >= CONSOLE_INTERACTION_X;
    const shouldBeNearPath = this.controlsReady
      && this.isNumberPathAvailable()
      && Math.abs(x - NUMBER_PATH_X) <= NUMBER_PATH_INTERACTION_RADIUS;
    const shouldBeNearCore = this.controlsReady
      && this.isNumberCoreAvailable()
      && Math.abs(x - NUMBER_CORE_X) <= NUMBER_CORE_INTERACTION_RADIUS;
    const shouldBeNearBridgeExit = this.controlsReady
      && this.isRestoredBridgeExitAvailable()
      && Math.abs(x - BRIDGE_X) <= BRIDGE_EXIT_INTERACTION_RADIUS;
    if (shouldBeNearBuilder === this.nearNumberBuilder
      && shouldBeNearPath === this.nearNumberPath
      && shouldBeNearCore === this.nearNumberCore
      && shouldBeNearBridgeExit === this.nearBridgeExit) return;
    this.nearNumberBuilder = shouldBeNearBuilder;
    this.nearNumberPath = shouldBeNearPath;
    this.nearNumberCore = shouldBeNearCore;
    this.nearBridgeExit = shouldBeNearBridgeExit;
    if (shouldBeNearBridgeExit) {
      this.reactionText.setText('Tap the restored Star Bridge or press E to cross.')
        .setAlpha(1)
        .setY(88);
    } else if (shouldBeNearCore) {
      this.reactionText.setText('Tap the Number Core or press E to inspect it.')
        .setAlpha(1)
        .setY(88);
    } else if (shouldBeNearPath) {
      this.reactionText.setText('Tap the Number Path or press E to begin.')
        .setAlpha(1)
        .setY(88);
    } else if (shouldBeNearBuilder) {
      this.reactionText.setText('Tap the Number Builder or press E to begin.')
        .setAlpha(1)
        .setY(88);
    } else if (this.reactionText.text.includes('Number Builder')
      || this.reactionText.text.includes('Number Path')
      || this.reactionText.text.includes('Number Core')
      || this.reactionText.text.includes('restored Star Bridge')) {
      this.reactionText.setAlpha(0);
    }
  }

  private isNumberBuilderAvailable() {
    return this.worldState.starBridgeDiscovered
      && this.worldState.starBridgeState === 'broken'
      && !this.repairSequenceRunning;
  }

  private isNumberPathAvailable() {
    return this.worldState.starBridgeDiscovered
      && this.worldState.starBridgeState === 'repair-1'
      && !this.repairSequenceRunning;
  }

  private isNumberCoreAvailable() {
    return this.worldState.starBridgeDiscovered
      && this.worldState.starBridgeState === 'repair-2'
      && !this.repairSequenceRunning
      && !this.numberCoreRevealRunning;
  }

  private isRestoredBridgeExitAvailable() {
    return this.worldState.bridgeCrossingUnlocked
      && this.worldState.starBridgeState === 'restored'
      && !this.repairSequenceRunning;
  }

  private updateChallengeAvailability() {
    this.updateConsoleAvailability();
    if (!this.numberPathPlatform) return;
    const available = this.isNumberPathAvailable();
    const completed = this.worldState.starBridgeState !== 'broken'
      && this.worldState.starBridgeState !== 'repair-1';
    this.numberPathPlatform.setAlpha(available ? 1 : completed ? 0.58 : 0);
    if (available && !this.reducedMotion && !this.activeNumberPath) {
      this.tweens.killTweensOf(this.numberPathPlatform);
      this.tweens.add({
        targets: this.numberPathPlatform,
        alpha: 0.82,
        scaleX: this.numberPathPlatform.scaleX * 1.025,
        scaleY: this.numberPathPlatform.scaleY * 1.025,
        duration: 1100,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    } else {
      this.tweens.killTweensOf(this.numberPathPlatform);
      this.numberPathPlatform.setDisplaySize(174, 119);
    }

    if (!this.numberCoreMachine) return;
    const coreAvailable = this.isNumberCoreAvailable();
    const coreComplete = this.worldState.starBridgeState === 'repair-3'
      || this.worldState.starBridgeState === 'restored';
    this.numberCoreMachine.setAlpha(coreAvailable ? 1 : coreComplete ? 0.8 : 0);
    if (coreComplete) {
      this.setWorldCoreTexture('powered');
      this.worldCoreCrystal.setVisible(true).setAlpha(0.82);
    } else if (coreAvailable && !this.numberCoreRevealed) {
      this.setWorldCoreTexture('machine');
      this.worldCoreCrystal.setVisible(false).setAlpha(0);
    }

    this.tweens.killTweensOf(this.numberCoreMachine);
    if (coreAvailable && !this.reducedMotion && !this.activeNumberCore && !this.numberCoreRevealRunning) {
      this.tweens.add({
        targets: this.numberCoreMachine,
        alpha: 0.84,
        scaleX: this.numberCoreMachine.scaleX * 1.02,
        scaleY: this.numberCoreMachine.scaleY * 1.02,
        duration: 1050,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    } else {
      this.numberCoreMachine.setDisplaySize(154, 168);
    }
    this.updateRestoredBridgeExitAvailability();
  }

  private updateRestoredBridgeExitAvailability() {
    if (!this.bridgeExitZone) return;
    const exit = resolveRestoredBridgeExit();
    this.registry.set('numberKingdom:restoredBridgeExitStatus', exit.status);
    this.registry.set('numberKingdom:restoredBridgeExitTarget', exit.targetDistrict);
    this.registry.set('numberKingdom:bridgeCrossingUnlocked', this.worldState.bridgeCrossingUnlocked);
    if (this.isRestoredBridgeExitAvailable()) {
      this.bridgeExitZone.setInteractive({ useHandCursor: true });
    } else {
      this.bridgeExitZone.disableInteractive();
    }
  }

  private setWorldCoreTexture(state: 'machine' | 'broken' | 'powered') {
    const definition = state === 'broken'
      ? { key: NUMBER_KINGDOM_ASSETS.numberCoreBroken.key, cropX: 30, cropW: 300 }
      : state === 'powered'
        ? { key: NUMBER_KINGDOM_ASSETS.numberCorePowered.key, cropX: 80, cropW: 305 }
        : { key: NUMBER_KINGDOM_ASSETS.numberCoreMachine.key, cropX: 0, cropW: 320 };
    this.numberCoreMachine.setTexture(definition.key)
      .setCrop(definition.cropX, 0, definition.cropW, 350)
      .setDisplaySize(154, 168);
  }

  private updateConsoleAvailability() {
    if (!this.numberBuilderConsole) return;
    const available = this.isNumberBuilderAvailable();
    this.numberBuilderConsole.setAlpha(
      available ? 1 : this.worldState.starBridgeDiscovered ? 0.78 : 0.42,
    );
    if (available && !this.missionVisible && !this.activeBuilder) this.startConsolePulse();
    else this.stopConsolePulse();
  }

  private startConsolePulse() {
    if (this.reducedMotion) {
      this.numberBuilderConsole.setAlpha(1).setDisplaySize(190, 116);
      return;
    }
    if (this.consolePulse?.isPlaying()) return;
    this.consolePulse = this.tweens.add({
      targets: this.numberBuilderConsole,
      scaleX: this.numberBuilderConsole.scaleX * 1.025,
      scaleY: this.numberBuilderConsole.scaleY * 1.025,
      alpha: 0.88,
      duration: this.reducedMotion ? 900 : 1150,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private stopConsolePulse() {
    this.consolePulse?.stop();
    this.consolePulse = undefined;
    if (!this.numberBuilderConsole) return;
    this.tweens.killTweensOf(this.numberBuilderConsole);
    this.numberBuilderConsole.setDisplaySize(190, 116);
  }

  private handleInteractKeyboard() {
    if (this.nearBridgeExit) this.tryUseRestoredBridgeExit(false);
    else if (this.nearNumberCore) this.tryActivateNumberCore(false);
    else if (this.nearNumberPath) this.tryActivateNumberPath(false);
    else if (this.nearNumberBuilder) this.tryActivateNumberBuilder(false);
  }

  private tryUseRestoredBridgeExit(fromPointer: boolean) {
    if (this.returningToHub || this.repairSequenceRunning) return;
    const exit = resolveRestoredBridgeExit();
    this.game.events.emit(RESTORED_BRIDGE_EXIT_EVENT, exit);
    if (exit.status === 'locked') {
      if (fromPointer) this.showReactionLine('Restore all three Star Bridge repairs first.');
      return;
    }
    if (exit.status === 'asset-gated' || !exit.sceneKey) {
      this.showReactionLine('The bridge leads to Kingdom Crossroads. The royal road is still being prepared!');
      return;
    }
    visitNumberKingdomDistrict(exit.targetDistrict);
    this.scene.start(exit.sceneKey);
  }

  private tryActivateNumberBuilder(fromPointer: boolean) {
    if (this.returningToHub || this.discoveryRunning || this.activeBuilder || this.activeNumberPath
      || this.activeNumberCore || this.repairSequenceRunning || this.numberCoreRevealRunning) return;
    if (!this.worldState.starBridgeDiscovered) {
      if (fromPointer) this.showReactionLine('Let’s inspect the broken Star Bridge first.');
      return;
    }
    if (this.worldState.starBridgeState !== 'broken') {
      if (fromPointer) this.showReactionLine('The first bridge repair is already powered!');
      return;
    }

    this.controlsReady = false;
    this.nearNumberBuilder = false;
    this.inputController.disable();
    this.stopConsolePulse();
    this.reactionText.setAlpha(0);
    this.tweens.killTweensOf([
      this.distantLayer,
      this.midgroundLayer,
      this.gameplayLayer,
      this.characterLayer,
      this.interactableLayer,
      this.foregroundLayer,
      this.uiLayer,
    ]);
    this.distantLayer.setAlpha(0.68);
    this.midgroundLayer.setAlpha(0.52);
    this.gameplayLayer.setAlpha(0.58);
    this.characterLayer.setAlpha(0);
    this.interactableLayer.setAlpha(0.45);
    this.foregroundLayer.setAlpha(0.3);
    this.uiLayer.setAlpha(0);

    this.activeBuilder = new CrystalNumberBuilder(
      this,
      STAR_BRIDGE_PLACE_VALUE_1,
      this.reducedMotion,
      {
        onCancel: () => {
          this.activeBuilder = undefined;
          this.restoreWorldAfterChallenge(true);
        },
        onComplete: config => {
          this.activeBuilder = undefined;
          this.beginFirstRepairSequence(config);
        },
      },
    );
  }

  private tryActivateNumberPath(fromPointer: boolean) {
    if (this.returningToHub || this.discoveryRunning || this.activeBuilder || this.activeNumberPath
      || this.activeNumberCore || this.repairSequenceRunning || this.numberCoreRevealRunning) return;
    if (this.worldState.starBridgeState === 'broken') {
      if (fromPointer) this.showReactionLine('Power the first bridge repair to awaken the Number Path.');
      return;
    }
    if (this.worldState.starBridgeState !== 'repair-1') {
      if (fromPointer) this.showReactionLine('The second bridge repair is already powered!');
      return;
    }

    this.controlsReady = false;
    this.nearNumberPath = false;
    this.inputController.disable();
    this.reactionText.setAlpha(0);
    this.tweens.killTweensOf([
      this.distantLayer, this.midgroundLayer, this.gameplayLayer, this.characterLayer,
      this.interactableLayer, this.foregroundLayer, this.uiLayer,
    ]);
    this.distantLayer.setAlpha(0.68);
    this.midgroundLayer.setAlpha(0.52);
    this.gameplayLayer.setAlpha(0.58);
    this.characterLayer.setAlpha(0.28);
    this.interactableLayer.setAlpha(0.45);
    this.foregroundLayer.setAlpha(0.3);
    this.uiLayer.setAlpha(0);

    this.activeNumberPath = new NumberPathChallenge(
      this,
      STAR_BRIDGE_NUMBER_PATH_2,
      this.reducedMotion,
      {
        onCancel: () => {
          this.activeNumberPath = undefined;
          this.restoreWorldAfterChallenge(true);
        },
        onComplete: config => {
          this.activeNumberPath = undefined;
          this.beginSecondRepairSequence(config);
        },
      },
    );
  }

  private tryActivateNumberCore(fromPointer: boolean) {
    if (this.returningToHub || this.discoveryRunning || this.activeBuilder
      || this.activeNumberPath || this.activeNumberCore || this.repairSequenceRunning
      || this.numberCoreRevealRunning) return;
    if (this.worldState.starBridgeState === 'broken' || this.worldState.starBridgeState === 'repair-1') {
      if (fromPointer) this.showReactionLine('Complete the earlier bridge repairs first.');
      return;
    }
    if (this.worldState.starBridgeState !== 'repair-2') {
      if (fromPointer) this.showReactionLine('The Number Core is fully powered!');
      return;
    }

    this.numberCoreRevealRunning = true;
    this.numberCoreRevealed = true;
    this.controlsReady = false;
    this.nearNumberCore = false;
    this.inputController.disable();
    this.tweens.killTweensOf(this.numberCoreMachine);
    this.setWorldCoreTexture('broken');
    this.numberCoreMachine.setAlpha(1);

    const pulse = this.add.image(
      NUMBER_CORE_X,
      NUMBER_CORE_Y - 92,
      NUMBER_KINGDOM_ASSETS.coreErrorPulse.key,
    ).setDepth(94).setAlpha(0);
    pulse.setCrop(0, 0, 290, 264);
    pulse.setDisplaySize(165, 150);
    this.tweens.add({
      targets: pulse,
      alpha: 0.9,
      scaleX: pulse.scaleX * (this.reducedMotion ? 1 : 1.08),
      scaleY: pulse.scaleY * (this.reducedMotion ? 1 : 1.08),
      duration: this.reducedMotion ? 140 : 330,
      yoyo: true,
      hold: this.reducedMotion ? 90 : 280,
      ease: 'Sine.easeInOut',
    });
    if (!this.reducedMotion) {
      this.tweens.add({
        targets: this.cosmo,
        angle: -4,
        scaleX: this.cosmo.scaleX * 1.04,
        scaleY: this.cosmo.scaleY * 1.04,
        duration: 260,
        yoyo: true,
        ease: 'Sine.easeInOut',
      });
    }
    this.showReactionLine("Uh-oh! The Number Core isn't showing the right number.");
    this.time.delayedCall(this.reducedMotion ? 600 : 1000, () => {
      this.showReactionLine("Let's find the part that needs fixing.");
    });
    this.time.delayedCall(this.reducedMotion ? 1150 : 1850, () => {
      pulse.destroy();
      if (this.returningToHub || !this.scene.isActive()) return;
      this.numberCoreRevealRunning = false;
      this.openNumberCoreChallenge();
    });
  }

  private openNumberCoreChallenge() {
    this.reactionText.setAlpha(0);
    this.tweens.killTweensOf([
      this.distantLayer, this.midgroundLayer, this.gameplayLayer, this.characterLayer,
      this.interactableLayer, this.foregroundLayer, this.uiLayer,
    ]);
    this.distantLayer.setAlpha(0.68);
    this.midgroundLayer.setAlpha(0.52);
    this.gameplayLayer.setAlpha(0.58);
    this.characterLayer.setAlpha(0.28);
    this.interactableLayer.setAlpha(0.45);
    this.foregroundLayer.setAlpha(0.3);
    this.uiLayer.setAlpha(0);

    this.activeNumberCore = new NumberCoreChallenge(
      this,
      STAR_BRIDGE_NUMBER_CORE_3,
      this.reducedMotion,
      {
        onCancel: () => {
          this.activeNumberCore = undefined;
          this.restoreWorldAfterChallenge(true);
        },
        onComplete: config => {
          this.activeNumberCore = undefined;
          this.beginFinalRestorationSequence(config);
        },
      },
    );
  }

  private restoreWorldAfterChallenge(enableControls: boolean) {
    const layers = [
      this.distantLayer,
      this.midgroundLayer,
      this.gameplayLayer,
      this.characterLayer,
      this.interactableLayer,
      this.foregroundLayer,
      this.uiLayer,
    ];
    this.tweens.killTweensOf(layers);
    this.tweens.add({
      targets: layers,
      alpha: 1,
      duration: this.reducedMotion ? 120 : 300,
      ease: 'Sine.easeOut',
    });
    if (!enableControls) return;
    this.controlsReady = true;
    this.inputController.enable();
    this.updateChallengeAvailability();
  }

  private checkDiscoveryTrigger(x: number) {
    if (!this.controlsReady || this.discoveryRunning || this.worldState.starBridgeDiscovered) return;
    if (x >= DISCOVERY_X) this.startBridgeDiscovery();
  }

  private startBridgeDiscovery() {
    this.discoveryRunning = true;
    this.controlsReady = false;
    this.inputController.disable();
    this.districtTitle.setText('The Broken Star Bridge');

    if (!this.reducedMotion) {
      this.cameras.main.zoomTo(this.renderScale * 1.035, 850, 'Sine.easeInOut');
      this.cameras.main.pan(700, 405, 850, 'Sine.easeInOut');
      this.tweens.add({
        targets: this.cosmo,
        scaleX: this.cosmo.scaleX * 1.04,
        scaleY: this.cosmo.scaleY * 1.04,
        angle: -3,
        duration: 380,
        ease: 'Back.easeOut',
      });
      this.tweens.add({
        targets: this.bridgeImage,
        alpha: 1,
        scaleX: this.bridgeImage.scaleX * 1.035,
        scaleY: this.bridgeImage.scaleY * 1.035,
        duration: 700,
        ease: 'Sine.easeOut',
      });
    } else {
      this.bridgeImage.setAlpha(1);
    }

    this.tweens.add({
      targets: this.bridgeGlow,
      alpha: this.reducedMotion ? 0.28 : 0.48,
      duration: this.reducedMotion ? 250 : 700,
      ease: 'Sine.easeOut',
    });

    this.time.delayedCall(this.reducedMotion ? 250 : 520, () => {
      this.showReactionLine('Whoa! The Star Bridge is broken!');
    });
    this.time.delayedCall(this.reducedMotion ? 1350 : 1750, () => {
      this.showReactionLine('We can rebuild it—three challenges will restore it!');
    });
    this.time.delayedCall(this.reducedMotion ? 2800 : 3400, () => {
      this.showMissionPanel();
    });
  }

  private showReactionLine(copy: string) {
    this.tweens.killTweensOf(this.reactionText);
    this.reactionText.setText(copy).setAlpha(0).setY(93);
    this.tweens.add({
      targets: this.reactionText,
      alpha: 1,
      y: this.reducedMotion ? 93 : 88,
      duration: this.reducedMotion ? 180 : 320,
      ease: 'Sine.easeOut',
    });
  }

  private showMissionPanel() {
    this.worldState = markStarBridgeDiscovered();
    this.registry.set('numberKingdom:starBridgeDiscovered', true);
    this.registry.set('numberKingdom:currentDistrict', this.worldState.currentDistrict);
    this.missionVisible = true;
    this.reactionText.setAlpha(0);
    this.missionPanel.setVisible(true).setAlpha(0).setScale(this.reducedMotion ? 1 : 0.94);
    this.missionPanel.setInteractive({ useHandCursor: true });
    this.input.keyboard?.on('keydown-ENTER', this.dismissMissionPanel, this);
    this.input.keyboard?.on('keydown-SPACE', this.dismissMissionPanel, this);
    this.tweens.add({
      targets: this.missionPanel,
      alpha: 1,
      scale: 1,
      duration: this.reducedMotion ? 220 : 380,
      ease: 'Back.easeOut',
    });
  }

  private dismissMissionPanel() {
    if (!this.missionVisible || this.returningToHub) return;
    this.missionVisible = false;
    this.removeMissionKeyboardListeners();
    this.missionPanel.disableInteractive();
    this.tweens.add({
      targets: this.missionPanel,
      alpha: 0,
      duration: this.reducedMotion ? 160 : 280,
      ease: 'Sine.easeIn',
      onComplete: () => this.missionPanel.setVisible(false),
    });
    this.tweens.add({
      targets: this.bridgeGlow,
      alpha: 0.08,
      duration: this.reducedMotion ? 180 : 450,
      ease: 'Sine.easeOut',
    });
    this.resetDiscoveryEmphasis();
  }

  private resetDiscoveryEmphasis() {
    const finish = () => {
      if (this.returningToHub) return;
      this.discoveryRunning = false;
      this.controlsReady = true;
      this.inputController.enable();
      this.updateChallengeAvailability();
    };
    if (this.reducedMotion) {
      finish();
      return;
    }

    this.cameras.main.zoomTo(this.renderScale, 520, 'Sine.easeInOut');
    this.cameras.main.pan(LAYOUT_W * 0.5, LAYOUT_H * 0.5, 520, 'Sine.easeInOut');
    this.tweens.add({
      targets: this.cosmo,
      displayWidth: COSMO_WIDTH,
      displayHeight: COSMO_HEIGHT,
      angle: 0,
      duration: 420,
      ease: 'Sine.easeOut',
    });
    this.setStarBridgeState(this.worldState.starBridgeState, false);
    this.time.delayedCall(540, finish);
  }

  private setStarBridgeState(state: StarBridgeState, animate = false) {
    const texture = BRIDGE_TEXTURES[state];
    const applyTexture = () => {
      this.bridgeImage.setTexture(texture);
      this.bridgeImage.setDisplaySize(
        BRIDGE_WIDTH,
        BRIDGE_WIDTH * (this.bridgeImage.height / this.bridgeImage.width),
      );
      this.registry.set('numberKingdom:starBridgeState', state);
    };

    this.tweens.killTweensOf(this.bridgeImage);
    if (!animate) {
      applyTexture();
      this.bridgeImage.setAlpha(1);
      return;
    }

    this.tweens.add({
      targets: this.bridgeImage,
      alpha: 0,
      duration: this.reducedMotion ? 130 : 260,
      ease: 'Sine.easeIn',
      onComplete: () => {
        applyTexture();
        this.tweens.add({
          targets: this.bridgeImage,
          alpha: 1,
          duration: this.reducedMotion ? 170 : 380,
          ease: 'Sine.easeOut',
        });
        this.tweens.add({
          targets: this.bridgeGlow,
          alpha: 0.55,
          yoyo: true,
          duration: this.reducedMotion ? 160 : 420,
          ease: 'Sine.easeInOut',
        });
      },
    });
  }

  private beginFirstRepairSequence(config: CrystalNumberBuilderConfig) {
    this.repairSequenceRunning = true;
    this.controlsReady = false;
    this.inputController.disable();
    this.restoreWorldAfterChallenge(false);
    const wasBroken = this.worldState.starBridgeState === 'broken';
    this.worldState = completeFirstStarBridgeRepair();
    if (wasBroken) addEnergy('number-kingdom', ENERGY_PER_SEGMENT);
    this.registry.set('numberKingdom:currentDistrict', this.worldState.currentDistrict);
    this.districtTitle.setText('Star Bridge — First Repair');

    const orb = this.add.image(
      CONSOLE_X,
      CONSOLE_Y - 88,
      NUMBER_KINGDOM_ASSETS.numberEnergyOrb.key,
    ).setDepth(92).setAlpha(0);
    orb.setCrop(10, 20, 295, 166);
    orb.setDisplaySize(170, 113);

    const beam = this.add.image(
      CONSOLE_X - 18,
      CONSOLE_Y - 96,
      NUMBER_KINGDOM_ASSETS.bridgeEnergyBeam.key,
    ).setDepth(91).setOrigin(0.12, 0.5).setAlpha(0);
    beam.setCrop(10, 16, 466, 174);
    beam.setDisplaySize(330, 146);
    beam.setRotation(Phaser.Math.Angle.Between(
      CONSOLE_X - 18,
      CONSOLE_Y - 96,
      BRIDGE_X,
      BRIDGE_Y,
    ));
    if (!this.reducedMotion) beam.setScale(beam.scaleX * 0.25, beam.scaleY);

    if (!this.reducedMotion) {
      this.cameras.main.zoomTo(this.renderScale * 1.025, 650, 'Sine.easeInOut');
      this.cameras.main.pan(790, 440, 650, 'Sine.easeInOut');
    }
    this.tweens.add({
      targets: orb,
      alpha: 1,
      scaleX: orb.scaleX * (this.reducedMotion ? 1 : 1.08),
      scaleY: orb.scaleY * (this.reducedMotion ? 1 : 1.08),
      duration: this.reducedMotion ? 180 : 480,
      ease: 'Sine.easeOut',
    });
    this.tweens.add({
      targets: this.numberBuilderConsole,
      scaleX: this.numberBuilderConsole.scaleX * (this.reducedMotion ? 1.01 : 1.06),
      scaleY: this.numberBuilderConsole.scaleY * (this.reducedMotion ? 1.01 : 1.06),
      duration: this.reducedMotion ? 180 : 420,
      yoyo: true,
      ease: 'Sine.easeInOut',
    });

    this.time.delayedCall(this.reducedMotion ? 300 : 520, () => {
      this.tweens.add({
        targets: beam,
        alpha: 1,
        scaleX: this.reducedMotion ? beam.scaleX : beam.scaleX * 4,
        duration: this.reducedMotion ? 180 : 620,
        ease: 'Sine.easeOut',
      });
    });

    this.time.delayedCall(this.reducedMotion ? 700 : 1050, () => {
      audio.bridgeSegment();
      this.setStarBridgeState(config.completionBridgeState, true);
      this.tweens.add({
        targets: this.bridgeGlow,
        alpha: 0.62,
        duration: this.reducedMotion ? 160 : 380,
        yoyo: true,
        hold: this.reducedMotion ? 80 : 260,
        ease: 'Sine.easeInOut',
      });
    });

    this.time.delayedCall(this.reducedMotion ? 1250 : 1700, () => {
      this.showReactionLine('First repair powered! A Number Path platform awakened!');
      if (!this.reducedMotion) {
        this.tweens.add({
          targets: this.cosmo,
          angle: -4,
          scaleX: this.cosmo.scaleX * 1.06,
          scaleY: this.cosmo.scaleY * 1.06,
          duration: 260,
          yoyo: true,
          repeat: 1,
          ease: 'Sine.easeInOut',
        });
      }
    });

    const finishDelay = this.reducedMotion ? 2350 : 3300;
    this.time.delayedCall(finishDelay, () => {
      this.tweens.add({
        targets: [orb, beam],
        alpha: 0,
        duration: this.reducedMotion ? 120 : 300,
        onComplete: () => {
          orb.destroy();
          beam.destroy();
        },
      });
      if (!this.reducedMotion) {
        this.cameras.main.zoomTo(this.renderScale, 440, 'Sine.easeInOut');
        this.cameras.main.pan(LAYOUT_W * 0.5, LAYOUT_H * 0.5, 440, 'Sine.easeInOut');
      }
      this.cosmo.setAngle(0).setDisplaySize(COSMO_WIDTH, COSMO_HEIGHT);
      this.reactionText.setAlpha(0);
      this.repairSequenceRunning = false;
      this.controlsReady = true;
      this.inputController.enable();
      this.updateChallengeAvailability();
    });
  }

  private beginSecondRepairSequence(config: NumberPathConfig) {
    this.repairSequenceRunning = true;
    this.controlsReady = false;
    this.inputController.disable();
    this.restoreWorldAfterChallenge(false);
    const wasFirstRepair = this.worldState.starBridgeState === 'repair-1';
    this.worldState = completeSecondStarBridgeRepair();
    if (wasFirstRepair) addEnergy('number-kingdom', ENERGY_PER_SEGMENT);
    this.registry.set('numberKingdom:currentDistrict', this.worldState.currentDistrict);
    this.districtTitle.setText('Star Bridge — Second Repair');

    const orb = this.add.image(
      NUMBER_PATH_X,
      NUMBER_PATH_Y - 82,
      NUMBER_KINGDOM_ASSETS.numberPathEnergyOrb.key,
    ).setDepth(93).setAlpha(0);
    orb.setCrop(8, 4, 222, 224);
    orb.setDisplaySize(88, 88);

    const beam = this.add.image(
      (NUMBER_PATH_X + BRIDGE_X) * 0.5,
      (NUMBER_PATH_Y + BRIDGE_Y) * 0.5 - 4,
      NUMBER_KINGDOM_ASSETS.bridgeEnergyBeam2.key,
    ).setDepth(92).setAlpha(0);
    beam.setCrop(0, 24, 376, 262);
    beam.setDisplaySize(360, 251);

    if (!this.reducedMotion) {
      this.cameras.main.zoomTo(this.renderScale * 1.025, 620, 'Sine.easeInOut');
      this.cameras.main.pan(710, 440, 620, 'Sine.easeInOut');
    }
    this.tweens.add({
      targets: orb,
      alpha: 1,
      scaleX: orb.scaleX * (this.reducedMotion ? 1 : 1.08),
      scaleY: orb.scaleY * (this.reducedMotion ? 1 : 1.08),
      duration: this.reducedMotion ? 170 : 430,
      ease: 'Sine.easeOut',
    });
    this.tweens.add({
      targets: this.numberPathPlatform,
      scaleX: this.numberPathPlatform.scaleX * (this.reducedMotion ? 1.01 : 1.06),
      scaleY: this.numberPathPlatform.scaleY * (this.reducedMotion ? 1.01 : 1.06),
      duration: this.reducedMotion ? 170 : 400,
      yoyo: true,
      ease: 'Sine.easeInOut',
    });

    this.time.delayedCall(this.reducedMotion ? 280 : 520, () => {
      this.tweens.add({
        targets: beam,
        alpha: 0.92,
        duration: this.reducedMotion ? 170 : 500,
        ease: 'Sine.easeOut',
      });
      this.tweens.add({
        targets: orb,
        x: BRIDGE_X,
        y: BRIDGE_Y + 18,
        duration: this.reducedMotion ? 260 : 720,
        ease: 'Sine.easeInOut',
      });
    });

    this.time.delayedCall(this.reducedMotion ? 700 : 1150, () => {
      audio.bridgeSegment();
      this.setStarBridgeState(config.completionBridgeState, true);
      this.tweens.add({
        targets: this.bridgeGlow,
        alpha: 0.68,
        duration: this.reducedMotion ? 160 : 390,
        yoyo: true,
        hold: this.reducedMotion ? 80 : 280,
        ease: 'Sine.easeInOut',
      });
    });

    this.time.delayedCall(this.reducedMotion ? 1280 : 1850, () => {
      this.showReactionLine('You powered the second Star Bridge repair!');
      if (!this.reducedMotion) {
        this.tweens.add({
          targets: this.cosmo,
          angle: 4,
          scaleX: this.cosmo.scaleX * 1.06,
          scaleY: this.cosmo.scaleY * 1.06,
          duration: 260,
          yoyo: true,
          repeat: 1,
          ease: 'Sine.easeInOut',
        });
      }
    });

    const finishDelay = this.reducedMotion ? 2350 : 3400;
    this.time.delayedCall(finishDelay, () => {
      this.tweens.add({
        targets: [orb, beam],
        alpha: 0,
        duration: this.reducedMotion ? 120 : 300,
        onComplete: () => {
          orb.destroy();
          beam.destroy();
        },
      });
      if (!this.reducedMotion) {
        this.cameras.main.zoomTo(this.renderScale, 440, 'Sine.easeInOut');
        this.cameras.main.pan(LAYOUT_W * 0.5, LAYOUT_H * 0.5, 440, 'Sine.easeInOut');
      }
      this.cosmo.setAngle(0).setDisplaySize(COSMO_WIDTH, COSMO_HEIGHT);
      this.reactionText.setAlpha(0);
      this.repairSequenceRunning = false;
      this.controlsReady = true;
      this.inputController.enable();
      this.updateChallengeAvailability();
    });
  }

  private beginFinalRestorationSequence(config: NumberCoreChallengeConfig) {
    this.repairSequenceRunning = true;
    this.controlsReady = false;
    this.inputController.disable();
    this.restoreWorldAfterChallenge(false);
    const wasSecondRepair = this.worldState.starBridgeState === 'repair-2';
    this.worldState = completeStarBridgeRestoration();
    if (wasSecondRepair) addEnergy('number-kingdom', ENERGY_PER_SEGMENT);
    this.registry.set('numberKingdom:currentDistrict', this.worldState.currentDistrict);
    this.districtTitle.setText('Star Bridge — Final Restoration');
    this.setWorldCoreTexture('powered');
    this.numberCoreMachine.setAlpha(1);
    this.worldCoreCrystal.setVisible(true).setAlpha(0);

    const coreBurst = this.add.image(
      NUMBER_CORE_X,
      NUMBER_CORE_Y - 90,
      NUMBER_KINGDOM_ASSETS.coreRepairBurst.key,
    ).setDepth(94).setAlpha(0);
    coreBurst.setCrop(0, 0, 313, 265);
    coreBurst.setDisplaySize(190, 161);

    const beam = this.add.image(
      (NUMBER_CORE_X + BRIDGE_X) * 0.5,
      (NUMBER_CORE_Y + BRIDGE_Y) * 0.5 - 10,
      NUMBER_KINGDOM_ASSETS.bridgeEnergyBeam3.key,
    ).setDepth(93).setAlpha(0);
    beam.setCrop(0, 0, 656, 112);
    beam.setDisplaySize(390, 112);

    const restorationBurst = this.add.image(
      BRIDGE_X,
      BRIDGE_Y + 5,
      NUMBER_KINGDOM_ASSETS.finalBridgeRestorationBurst.key,
    ).setDepth(95).setAlpha(0).setVisible(false);
    restorationBurst.setDisplaySize(380, 156);

    if (!this.reducedMotion) {
      this.cameras.main.zoomTo(this.renderScale * 1.035, 720, 'Sine.easeInOut');
      this.cameras.main.pan(660, 430, 720, 'Sine.easeInOut');
    }
    this.tweens.add({
      targets: [coreBurst, this.worldCoreCrystal],
      alpha: 1,
      duration: this.reducedMotion ? 160 : 430,
      ease: 'Sine.easeOut',
    });
    if (!this.reducedMotion) {
      this.tweens.add({
        targets: this.worldCoreCrystal,
        scaleX: this.worldCoreCrystal.scaleX * 1.08,
        scaleY: this.worldCoreCrystal.scaleY * 1.08,
        duration: 520,
        yoyo: true,
        repeat: 1,
        ease: 'Sine.easeInOut',
      });
    }

    this.time.delayedCall(this.reducedMotion ? 300 : 620, () => {
      this.tweens.add({
        targets: beam,
        alpha: 0.96,
        duration: this.reducedMotion ? 170 : 480,
        ease: 'Sine.easeOut',
      });
    });

    const repairThreeDelay = this.reducedMotion ? 550 : 1200;
    this.time.delayedCall(repairThreeDelay, () => {
      audio.bridgeSegment();
      this.setStarBridgeState(config.completionBridgeState, true);
      this.showReactionLine('The third Star Bridge repair is complete!');
      this.tweens.add({
        targets: this.bridgeGlow,
        alpha: 0.74,
        duration: this.reducedMotion ? 160 : 420,
        yoyo: true,
        hold: this.reducedMotion ? 180 : 420,
        ease: 'Sine.easeInOut',
      });
    });

    const finalBurstDelay = this.reducedMotion ? 1700 : 2850;
    this.time.delayedCall(finalBurstDelay, () => {
      restorationBurst.setVisible(true).setAlpha(0).setScale(this.reducedMotion ? 1 : 0.82);
      this.tweens.add({
        targets: restorationBurst,
        alpha: 1,
        scale: 1,
        duration: this.reducedMotion ? 190 : 520,
        ease: 'Back.easeOut',
      });
      this.tweens.add({
        targets: this.bridgeGlow,
        alpha: 0.88,
        duration: this.reducedMotion ? 180 : 520,
        ease: 'Sine.easeOut',
      });
    });

    const restoredDelay = this.reducedMotion ? 2150 : 3500;
    this.time.delayedCall(restoredDelay, () => {
      this.setStarBridgeState('restored', true);
      this.showReactionLine('The Star Bridge is fully restored!');
      if (!this.reducedMotion) {
        this.tweens.add({
          targets: this.cosmo,
          angle: -5,
          scaleX: this.cosmo.scaleX * 1.08,
          scaleY: this.cosmo.scaleY * 1.08,
          duration: 280,
          yoyo: true,
          repeat: 2,
          ease: 'Sine.easeInOut',
        });
      }
    });

    const finishDelay = this.reducedMotion ? 3200 : 5600;
    this.time.delayedCall(finishDelay, () => {
      this.tweens.add({
        targets: [coreBurst, beam, restorationBurst],
        alpha: 0,
        duration: this.reducedMotion ? 120 : 320,
        onComplete: () => {
          coreBurst.destroy();
          beam.destroy();
          restorationBurst.destroy();
        },
      });
      if (!this.reducedMotion) {
        this.cameras.main.zoomTo(this.renderScale, 460, 'Sine.easeInOut');
        this.cameras.main.pan(LAYOUT_W * 0.5, LAYOUT_H * 0.5, 460, 'Sine.easeInOut');
      }
      this.cosmo.setAngle(0).setDisplaySize(COSMO_WIDTH, COSMO_HEIGHT);
      this.reactionText.setAlpha(0);
      this.repairSequenceRunning = false;
      this.controlsReady = true;
      if (document.hasFocus()) this.inputController.enable();
      this.updateChallengeAvailability();
    });
  }

  private handleEscape() {
    if (this.repairSequenceRunning || this.numberCoreRevealRunning) return;
    if (this.activeNumberCore) {
      this.activeNumberCore.destroy();
      this.activeNumberCore = undefined;
      this.restoreWorldAfterChallenge(true);
      return;
    }
    if (this.activeNumberPath) {
      this.activeNumberPath.destroy();
      this.activeNumberPath = undefined;
      this.restoreWorldAfterChallenge(true);
      return;
    }
    if (this.activeBuilder) {
      this.activeBuilder.destroy();
      this.activeBuilder = undefined;
      this.restoreWorldAfterChallenge(true);
      return;
    }
    if (this.missionVisible) {
      this.dismissMissionPanel();
      return;
    }
    this.returnToHub();
  }

  private returnToHub() {
    if (this.returningToHub) return;
    this.returningToHub = true;
    this.controlsReady = false;
    this.inputController.disable();
    this.activeBuilder?.destroy();
    this.activeBuilder = undefined;
    this.activeNumberPath?.destroy();
    this.activeNumberPath = undefined;
    this.activeNumberCore?.destroy();
    this.activeNumberCore = undefined;
    this.stopConsolePulse();
    this.removeMissionKeyboardListeners();
    this.input.keyboard?.off('keydown-ESC', this.handleEscape, this);
    this.input.keyboard?.off('keydown-E', this.handleInteractKeyboard, this);
    this.cameras.main.fadeOut(this.reducedMotion ? 180 : 380, 255, 248, 220);
    this.time.delayedCall(this.reducedMotion ? 200 : 400, () => this.scene.start('HomeHub'));
  }

  private registerLifecycleSafety() {
    this.unsubscribeLifecycle = applicationLifecycle.subscribe({
      onSuspend: () => this.handlePause(),
      onResume: () => this.handleResume(),
    });
    window.addEventListener('resize', this.handleWindowResize);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.unsubscribeLifecycle?.();
      this.unsubscribeLifecycle = undefined;
      audio.leaveSoundscape({ id: 'number-kingdom' });
      window.removeEventListener('resize', this.handleWindowResize);
      if (this.resizeDebounce !== undefined) window.clearTimeout(this.resizeDebounce);
      this.input.keyboard?.off('keydown-ESC', this.handleEscape, this);
      this.input.keyboard?.off('keydown-E', this.handleInteractKeyboard, this);
      this.removeMissionKeyboardListeners();
      this.activeBuilder?.destroy();
      this.activeBuilder = undefined;
      this.activeNumberPath?.destroy();
      this.activeNumberPath = undefined;
      this.activeNumberCore?.destroy();
      this.activeNumberCore = undefined;
      this.stopConsolePulse();
      this.tweens.killAll();
    });
  }

  private removeMissionKeyboardListeners() {
    this.input.keyboard?.off('keydown-ENTER', this.dismissMissionPanel, this);
    this.input.keyboard?.off('keydown-SPACE', this.dismissMissionPanel, this);
  }

  private handlePause() {
    this.inputController.disable();
    this.activeBuilder?.setSuspended(true);
    this.activeNumberPath?.setSuspended(true);
    this.activeNumberCore?.setSuspended(true);
  }

  private readonly handleWindowResize = () => {
    if (this.resizeDebounce !== undefined) window.clearTimeout(this.resizeDebounce);
    this.resizeDebounce = window.setTimeout(() => {
      if (!this.scene.isActive()) return;
      this.renderScale = this.configureCrispRenderer();
      this.positionSafeUI();
      this.activeBuilder?.layout();
      this.activeNumberPath?.layout();
      this.activeNumberCore?.layout();
    }, 80);
  };

  private positionSafeUI() {
    const { visible, mode } = getSceneResponsiveLayout(this, NUMBER_KINGDOM_EXPLORATION_LAYOUT);
    this.homeButton.layout(visible, mode === 'compact', 'left');
    this.controlsHint.setX(visible.right - 18);
  }

  private handleResume() {
    if (this.activeBuilder) {
      this.activeBuilder.setSuspended(false);
      return;
    }
    if (this.activeNumberPath) {
      this.activeNumberPath.setSuspended(false);
      return;
    }
    if (this.activeNumberCore) {
      this.activeNumberCore.setSuspended(false);
      return;
    }
    if (this.controlsReady && !this.returningToHub && !this.missionVisible) {
      this.inputController.enable();
    }
  }
}
